import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@emotion/react";

import { listTickets } from "works/higurashi/utils/api";

import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";
import Header from "works/higurashi/components/Header";
import AppLayout from "works/higurashi/components/AppLayout";
import Error from "default/components/Error";
import InputEmail from "default/components/InputEmail";
import PayPayButton from "default/components/PayPayButton";
import StyledButton from "default/components/StyledButton";
import PayPayConfirm from "works/higurashi/components/PayPayConfirm";
import TicketItem from "works/higurashi/components/TicketItem";
import { useBasicInfo } from "../hooks/useBasicInfo";
import { useStateInfo } from "../hooks/useStateInfo";
import { usePurchase, PURCHASE_METHODS } from "../hooks/usePurchase";
import { getHigurashiSouls } from "works/higurashi/utils/characters";

import { getUnconfirmedPayPayPayment } from "works/higurashi/utils/api";

const SOULCHAT_QA = [
  {
    question: {
      ja: "途中でチケットの期間が終わったらどうなりますか？",
      en: "",
      "zh-tw": "",
    },
    answer: {
      ja: "期限が終わった後に同類のチケットを購入することで、記憶とステータスを引き継いだまま交流を継続できます。期間が終わるとチケット購入ページに購入可能なチケットが自動で表示されます。",
      en: "",
      "zh-tw": "",
    },
  },
  {
    question: {
      ja: "レナとの交流チケットと雛見沢パスは同時に購入できますか？",
      en: "",
      "zh-tw": "",
    },
    answer: {
      ja: "いいえ、同時には購入できません。\nレナとの交流チケットはレナだけと交流する体験、雛見沢パスはレナ・魅音・圭一と交流する体験です。進行中の体験はどちらか一方のみとなるため、一方の有効期間中はもう一方を購入できません。期間終了後に、別の体験を選ぶことができます。",
      en: "",
      "zh-tw": "",
    },
  },
  {
    question: {
      ja: "作品データは安全に管理されているんですか？",
      en: "",
      "zh-tw": "",
    },
    answer: {
      ja: "SoulChatサービスにおいて、作品データおよび会話データが第三者が利用できるAIに反映されることは一切ございません。\n自社のAIモデルの場合には、自社管理のサーバー内で学習および提供の全工程が完結しており、他社様が提供するAIサービスを利用する場合においては、入力データがモデルの学習に使用されないことが契約上保証されている有料サービスのみを採用しております。\nコンテンツの制作から提供に至る全過程において、作品データが外部のAI学習に利用される経路は存在いたしません。\nSoulChatでは今後も作品の著作権と権利を尊重し、それらが守られることを前提にコンテンツを制作致します。",
      en: "",
      "zh-tw": "",
    },
  },
];

const getQAText = (item, field, language) => {
  const value = item?.[field]?.[language];
  if (value) return value;
  return item?.[field]?.ja || "";
};

const getTicketDurationDays = (ticket) => {
  const candidates = [
    ticket.name,
    ticket.ticketKey,
    ticket.data?.data?.ticket_key,
    ticket.data?.ticket_key,
  ];

  for (const value of candidates) {
    const match = String(value || "").match(/(\d+)\s*(?:日|days?)/i);
    if (match) return Number(match[1]);
  }

  return null;
};

const getFeaturedPassOrder = (ticket) => {
  const name = ticket.name || "";

  if (name.includes("雛見沢見学パス") || name.includes("雛見沢パス")) {
    return 0;
  }

  if (name.includes("レナとの交流パス") || name.includes("レナとの交流チケット")) {
    return 1;
  }

  return null;
};

const sortTicketsByDurationDesc = (tickets) => {
  return tickets
    .map((ticket, index) => ({
      ticket,
      index,
      durationDays: getTicketDurationDays(ticket),
      featuredPassOrder: getFeaturedPassOrder(ticket),
    }))
    .sort((a, b) => {
      if (
        a.featuredPassOrder !== null &&
        b.featuredPassOrder !== null &&
        a.featuredPassOrder !== b.featuredPassOrder
      ) {
        return a.featuredPassOrder - b.featuredPassOrder;
      }

      if (a.durationDays !== null && b.durationDays !== null) {
        return b.durationDays - a.durationDays || a.index - b.index;
      }

      if (a.durationDays !== null) return -1;
      if (b.durationDays !== null) return 1;
      return a.index - b.index;
    })
    .map(({ ticket }) => ticket);
};

const getTicketPurchaseData = (ticket) => ticket?.data?.data || {};

const isRepurchaseTicket = (ticket) => {
  const purchaseData = getTicketPurchaseData(ticket);
  return (
    ticket?.ticketType === "repurchase" ||
    purchaseData.ticket_type === "repurchase"
  );
};

const shouldShowTicketSwitchWarning = (ticket, currentState) => {
  if (!currentState || isRepurchaseTicket(ticket)) return false;

  const { purchase_action: purchaseAction } = getTicketPurchaseData(ticket);
  return purchaseAction === "start" || purchaseAction === "start_fresh";
};

// Custom hooks
const useTickets = (accountKey) => {
  const [state, setState] = useState({
    tickets: [],
    isLoading: true,
    isError: false,
  });

  const loadTickets = async (accountKey) => {
    if (!accountKey) return;

    try {
      const tickets = await listTickets(accountKey);
      setState((prev) => ({ ...prev, tickets, isError: false }));
    } catch (error) {
      console.error("Error loading tickets:", error);
      setState((prev) => ({ ...prev, isError: true }));
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  useEffect(() => {
    loadTickets(accountKey);
  }, [accountKey]);

  return state;
};

const usePaymentCheck = (accountKey, work) => {
  const navigate = useNavigate();
  const [state, setState] = useState({
    merchantPaymentId: null,
    isPaymentCheckNeeded: false,
  });

  const checkUnconfirmedPayment = async (accountKey) => {
    try {
      const { merchantPaymentId } = await getUnconfirmedPayPayPayment(
        accountKey
      );

      if (merchantPaymentId) {
        setState((prev) => ({
          ...prev,
          merchantPaymentId,
          isPaymentCheckNeeded: true,
        }));
      }
    } catch (error) {
      console.error("Error checking unconfirmed payment:", error);
    }
  };

  const checkPaymentStatus = useCallback(() => {
    navigate(
      `/${work}/paypay/check?merchant_payment_id=${state.merchantPaymentId}&account=${accountKey}`
    );
  }, [navigate, work, state.merchantPaymentId, accountKey]);

  useEffect(() => {
    if (accountKey) {
      checkUnconfirmedPayment(accountKey);
    }
  }, [accountKey]);

  return {
    isPaymentCheckNeeded: state.isPaymentCheckNeeded,
    checkPaymentStatus,
  };
};

const StorySection = ({ isExpired, chapter, t }) => {
  const header = !isExpired ? (
    <Header
      title="Story"
      subtitle={t(`chapter.${chapter}`)}
      highlightedWord="r"
    />
  ) : (
    <Header
      title="Interlude"
      subtitle={t(`chapter.${chapter}`)}
      highlightedWord="d"
    />
  );

  return (
    <>
      {header}
      <Typography
        sx={{
          whiteSpace: "pre-line",
          textAlign: "center",
          lineHeight: 2.4,
          fontWeight: 600,
          fontSize: 17,
          fontFamily: "'Noto Serif JP', serif",
        }}
      >
        {!isExpired
          ? t(`chapterStory.${chapter}.story`)
          : t(`chapterStory.${chapter}.interlude`)}
      </Typography>
    </>
  );
};

// Component for Soul Item
const SoulItem = ({ soul }) => {
  const isClickable = Boolean(soul.lineUrl);

  return (
    <Box
      component={isClickable ? "a" : "div"}
      href={isClickable ? soul.lineUrl : undefined}
      sx={{
        display: "flex",
        alignItems: "center", // Reverted to center alignment
        width: "76%",
        maxWidth: 600,
        marginBottom: 5,
        color: "inherit",
        textDecoration: "none",
        cursor: isClickable ? "pointer" : "default",
        transition: isClickable ? "opacity 160ms ease" : undefined,
        "&:visited": {
          color: "inherit",
        },
        "&:focus": {
          color: "inherit",
          textDecoration: "none",
        },
        "&:hover": isClickable
          ? {
              opacity: 0.84,
              color: "inherit",
              textDecoration: "none",
            }
          : undefined,
        "&:active": isClickable
          ? {
              opacity: 0.72,
              color: "inherit",
              textDecoration: "none",
            }
          : undefined,
      }}
    >
      {/* Soul Image */}
      <Box
        sx={{
          width: 90,
          height: 90,
          borderRadius: "50%",
          overflow: "hidden",
          marginRight: 2.5,
          flexShrink: 0,
          boxShadow: "0 0 20px #BF2120",
        }}
      >
        <img
          src={soul.imageURL}
          alt={soul.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </Box>

      {/* Soul Info */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
        }}
      >
        {/* Soul Name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginBottom: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: "white",
              fontFamily: "'Noto Serif JP', serif",
            }}
          >
            {soul.name}
          </Typography>
          {isClickable && (
            <ArrowForwardIosIcon
              aria-hidden="true"
              sx={{
                ml: 0.75,
                width: 22,
                height: 22,
                color: "#BF2120",
                opacity: 0.92,
                transform: "translateY(1px) scaleX(1.08)",
                filter: "drop-shadow(0 0 5px rgba(191, 33, 32, 0.5))",
              }}
            />
          )}
        </Box>

        {/* Soul Description */}
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: 1.8,
            fontWeight: 400, // Regular
            color: "rgba(255, 255, 255, 0.9)",
            whiteSpace: "pre-line",
            fontFamily: "'Noto Serif JP', serif",
          }}
        >
          {soul.description}
        </Typography>
      </Box>
    </Box>
  );
};

// Component for Souls Section
const SoulsSection = ({ souls, t }) => {
  return (
    <Box sx={{ width: "100%" }}>
      {/* Section Header */}
      <Header title="Souls" highlightedWord="o" />

      {/* Soul Cards */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {souls.map((soul) => (
          <SoulItem key={soul.name} soul={soul} />
        ))}
      </Box>
    </Box>
  );
};

// Component for Ticket Section
const TicketSection = ({ tickets, onClickTicket, t }) => {
  return (
    <Box>
      <Header title="Tickets" highlightedWord="c" sx={{ margin: 0 }} />
      {tickets.length === 0 ? (
        <Typography
          sx={(theme) => ({
            whiteSpace: "pre-line",
            lineHeight: 2.0,
            fontFamily: theme.system.fonts.body,
            color: theme.system.colors.text.primary,
            fontSize: theme.system.fontSizes.medium,
          })}
        >
          {t("common:error.ticket_not_available")}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          width: "100%",
        }}
      >
        {tickets.map((ticket) => (
          <TicketItem
            key={ticket.ticketKey}
            ticket={ticket}
            onClick={() => onClickTicket(ticket)}
          />
        ))}
      </Box>
    </Box>
  );
};

const QASection = ({ language }) => {
  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        px: 3,
        pt: 3,
        pb: 1,
        boxSizing: "border-box",
      }}
    >
      <Header title="Q&A" highlightedWord="Q" />
      <Box
        sx={{
          width: "100%",
          maxWidth: 520,
          mx: "auto",
        }}
      >
        {SOULCHAT_QA.map((item, index) => (
          <Accordion
            key={index}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              color: "white",
              borderRadius: "8px",
              overflow: "hidden",
              "&:before": {
                display: "none",
              },
              "&.Mui-expanded": {
                margin: 0,
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon sx={{ color: "rgba(255, 255, 255, 0.72)" }} />
              }
              sx={{
                px: 2,
                py: 0.5,
                minHeight: 56,
                "&.Mui-expanded": {
                  minHeight: 56,
                },
                "& .MuiAccordionSummary-content": {
                  my: 1.5,
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  my: 1.5,
                },
              }}
            >
              <Typography
                sx={(theme) => ({
                  fontFamily: theme.system.fonts.body,
                  fontSize: "14px",
                  fontWeight: 700,
                  lineHeight: 1.6,
                  color: theme.system.colors.text.primary,
                })}
              >
                Q. {getQAText(item, "question", language)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
              <Typography
                sx={(theme) => ({
                  whiteSpace: "pre-line",
                  fontFamily: theme.system.fonts.body,
                  fontSize: "12px",
                  lineHeight: 1.8,
                  color: theme.system.colors.text.secondary,
                })}
              >
                {getQAText(item, "answer", language)}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

// Component for Purchase Dialog
const PurchaseDialog = ({
  open,
  onClose,
  onClickPurchase,
  isProcessing,
  lng,
  t,
}) => {
  const stripeButton = (
    <StyledButton
      key="credit-card"
      variant="contained"
      onClick={() => onClickPurchase(PURCHASE_METHODS.STRIPE)}
      sx={(theme) => ({
        width: "100%",
        ...theme.components.button,
        // fontSize: "16px",
        // color: theme.Dialog.button.color,
        // border: `2px solid ${theme.Dialog.button.color}`,
        // background: isProcessing ? "white" : "transparent",
      })}
      disabled={isProcessing}
    >
      {t("common:button.purchase_option_credit_card")}
    </StyledButton>
  );

  const payPayBox = (
    <Box key="paypay" sx={{ width: "100%" }}>
      <PayPayButton
        onClick={() => onClickPurchase(PURCHASE_METHODS.PAYPAY)}
        disabled={isProcessing}
      />
      <Typography
        sx={(theme) => ({
          mt: 1,
          textAlign: "center",
          whiteSpace: "pre-line",
          fontSize: "12px",
        })}
      >
        {t("common:button.purchase_option_paypay")}
      </Typography>
    </Box>
  );

  const rawButtons = lng === "ja" ? [stripeButton, payPayBox] : [stripeButton];

  return (
    <ButtonDialog
      open={open}
      title={t("common:button.dialog.select_purchase")}
      rawButtons={rawButtons}
      handleClose={onClose}
    />
  );
};

// Main Component
function Tickets() {
  const theme = useTheme();
  const {
    work,
    chapter,
    lng,
    visualStyle,
    accountKey,
    state,
  } = useBasicInfo();

  const { isExpired } = useStateInfo(state);

  const { t } = useTranslation([work, "common"]);

  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  const [switchWarningTicket, setSwitchWarningTicket] = useState(null);

  const { tickets, isError, isLoading } = useTickets(accountKey);
  const sortedTickets = useMemo(
    () => sortTicketsByDurationDesc(tickets),
    [tickets]
  );

  const {
    isProcessing,
    emailRequired,
    paypayConfirmRequired,
    setPurchaseData,
    handlePurchase,
    handleConfirmPayPay,
    navigateToPurchase,
  } = usePurchase();

  const { isPaymentCheckNeeded, checkPaymentStatus } = usePaymentCheck(
    accountKey,
    work
  );

  const visibleSouls = getHigurashiSouls({ visualStyle, lng });

  // Event handlers
  const openPurchaseFlow = useCallback(
    (ticket) => {
      setPurchaseData(ticket.data);
      setOpenPurchaseDialog(true);
    },
    [setPurchaseData]
  );

  const handleClickTicket = useCallback(
    (ticket) => {
      if (shouldShowTicketSwitchWarning(ticket, state)) {
        setSwitchWarningTicket(ticket);
        return;
      }

      openPurchaseFlow(ticket);
    },
    [openPurchaseFlow, state]
  );

  const handleConfirmSwitchWarning = useCallback(() => {
    if (switchWarningTicket) {
      openPurchaseFlow(switchWarningTicket);
    }
    setSwitchWarningTicket(null);
  }, [openPurchaseFlow, switchWarningTicket]);

  const handleCloseSwitchWarning = useCallback(() => {
    setSwitchWarningTicket(null);
  }, []);

  const handleClickPurchase = useCallback(
    (method) => {
      handlePurchase(method);
      setOpenPurchaseDialog(false);
    },
    [handlePurchase]
  );

  const handlePaymentCheckClose = useCallback(() => {
    // This would need to be implemented in the useTickets hook
    // For now, we'll just navigate or close
  }, []);

  // Render loading state
  if (isLoading) {
    return (
      <AppLayout page="Tickets" width="100%">
        <Loading />
      </AppLayout>
    );
  }

  // Render error state
  if (isError) {
    return (
      <AppLayout page="Tickets" width="100%">
        <Error />
      </AppLayout>
    );
  }

  // Render email input state
  if (emailRequired) {
    return (
      <AppLayout page="Tickets" width="80%">
        <InputEmail onClickPurchase={navigateToPurchase} />
      </AppLayout>
    );
  }

  if (paypayConfirmRequired) {
    return (
      <AppLayout page="Tickets" width="80%">
        <PayPayConfirm onConfirm={handleConfirmPayPay} />
      </AppLayout>
    );
  }

  return (
    <AppLayout page="Tickets" width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          width: "100%",
          mb: 5,
        }}
      >
        {/* Hero Image */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <img
            src={theme.assets.heroImage}
            alt="hero"
            width="100%"
            style={{ display: "block" }}
          />
        </Box>

        {/* Story Section */}
        <StorySection isExpired={isExpired} chapter={chapter} t={t} />

        {/* Souls Section */}
        <SoulsSection
          souls={visibleSouls}
          t={t}
        />

        {/* Tickets Section */}
        <TicketSection
          tickets={sortedTickets}
          onClickTicket={handleClickTicket}
          t={t}
        />

        {/* Q&A Section */}
        <QASection language={lng} />
      </Box>

      {/* Purchase Method Dialog */}
      <PurchaseDialog
        open={openPurchaseDialog}
        onClose={() => setOpenPurchaseDialog(false)}
        onClickPurchase={handleClickPurchase}
        isProcessing={isProcessing}
        lng={lng}
        t={t}
      />

      {/* Ticket Switch Warning Dialog */}
      <ButtonDialog
        open={Boolean(switchWarningTicket)}
        title="チケット変更時の注意"
        description="異なるタイプのチケットを購入した場合、以前のチケットでのキャラクターの記憶・クエスト進行状況は引き継がれません。"
        handleClose={handleCloseSwitchWarning}
        buttons={[
          {
            label: "確認して進む",
            onClick: handleConfirmSwitchWarning,
          },
        ]}
      />

      {/* Payment Check Dialog */}
      <ButtonDialog
        open={isPaymentCheckNeeded}
        title="PayPay購入による処理が正常に完了していない可能性があります。"
        handleClose={handlePaymentCheckClose}
        buttons={[
          {
            label: "購入状況を確認",
            onClick: checkPaymentStatus,
          },
        ]}
      />
    </AppLayout>
  );
}

export default Tickets;
